
import { useState, useRef } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { UseFormReturn } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Upload, X, Image } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

interface ImageUploadProps {
  form: UseFormReturn<any>;
}

export const ImageUpload = ({ form }: ImageUploadProps) => {
  const { t, language } = useLanguage();
  const [uploading, setUploading] = useState<boolean>(false);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  
  const MAX_FILES = 4;
  const MAX_FILE_SIZE = 2.5 * 1024 * 1024; // 2.5MB
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const currentImages = form.getValues('image_urls') || [];
    
    // Check if adding these files would exceed the limit
    if (currentImages.length + files.length > MAX_FILES) {
      toast({
        variant: "destructive",
        title: language === 'en' ? "Too many files" : "檔案過多",
        description: language === 'en' 
          ? `You can upload a maximum of ${MAX_FILES} images` 
          : `最多只能上傳 ${MAX_FILES} 張圖片`
      });
      return;
    }
    
    setUploading(true);
    
    const imageUrls: string[] = [...currentImages];
    const tempPreviewUrls: string[] = [...previewUrls];
    
    try {
      // Process each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          toast({
            variant: "destructive",
            title: language === 'en' ? "File too large" : "檔案太大",
            description: language === 'en' 
              ? `File ${file.name} exceeds the 2.5MB limit` 
              : `檔案 ${file.name} 超過 2.5MB 限制`
          });
          continue;
        }
        
        // Generate a unique file name
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
        const filePath = `${fileName}`;
        
        // Create object URL for preview
        const objectUrl = URL.createObjectURL(file);
        tempPreviewUrls.push(objectUrl);
        
        // Upload to Supabase Storage
        const { data, error } = await supabase.storage
          .from('review_images')
          .upload(filePath, file);
        
        if (error) {
          console.error('Error uploading file:', error);
          toast({
            variant: "destructive",
            title: language === 'en' ? "Upload failed" : "上傳失敗",
            description: error.message
          });
        } else {
          // Get public URL
          const { data: publicUrlData } = supabase.storage
            .from('review_images')
            .getPublicUrl(filePath);
          
          if (publicUrlData?.publicUrl) {
            imageUrls.push(publicUrlData.publicUrl);
          }
        }
      }
      
      // Update the form value
      form.setValue('image_urls', imageUrls);
      setPreviewUrls(tempPreviewUrls);
      
      if (imageUrls.length > currentImages.length) {
        toast({
          title: language === 'en' ? "Image uploaded" : "圖片已上傳",
          description: language === 'en' 
            ? `${imageUrls.length - currentImages.length} images uploaded successfully` 
            : `${imageUrls.length - currentImages.length} 張圖片上傳成功`
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        variant: "destructive",
        title: language === 'en' ? "Upload error" : "上傳錯誤",
        description: String(error)
      });
    } finally {
      setUploading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const removeImage = async (index: number) => {
    const currentImages = form.getValues('image_urls') || [];
    
    if (index >= 0 && index < currentImages.length) {
      // Extract the filename from the URL
      const url = currentImages[index];
      const fileName = url.split('/').pop();
      
      // Remove from Supabase Storage
      if (fileName) {
        try {
          const { error } = await supabase.storage
            .from('review_images')
            .remove([fileName]);
          
          if (error) {
            console.error('Error removing file:', error);
          }
        } catch (err) {
          console.error('Error during file removal:', err);
        }
      }
      
      // Update the form value
      const updatedImages = [...currentImages];
      updatedImages.splice(index, 1);
      form.setValue('image_urls', updatedImages);
      
      // Update preview URLs
      const updatedPreviews = [...previewUrls];
      updatedPreviews.splice(index, 1);
      setPreviewUrls(updatedPreviews);
      
      toast({
        title: language === 'en' ? "Image removed" : "圖片已移除",
        description: language === 'en' ? "Image removed successfully" : "圖片已成功移除"
      });
    }
  };
  
  return (
    <FormField
      control={form.control}
      name="image_urls"
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {language === 'en' ? 'Upload Property Images' : '上傳房屋照片'}
            <span className="text-sm text-muted-foreground ml-2">
              ({language === 'en' ? 'Maximum 4 images, 2.5MB each' : '最多 4 張，每張 2.5MB'})
            </span>
          </FormLabel>
          <FormControl>
            <div className="space-y-4">
              <input 
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                ref={fileInputRef}
                className="hidden"
              />
              
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || (field.value && field.value.length >= MAX_FILES)}
                className="w-full h-20 border-dashed"
              >
                {uploading ? (
                  <div className="flex items-center">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2"></div>
                    {language === 'en' ? 'Uploading...' : '上傳中...'}
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="h-6 w-6 mb-2" />
                    {language === 'en' ? 'Click to upload images' : '點擊上傳圖片'}
                  </div>
                )}
              </Button>
              
              {/* Image Preview */}
              {(field.value && field.value.length > 0) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {field.value.map((url: string, index: number) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square rounded border overflow-hidden bg-gray-50">
                        <img 
                          src={url}
                          alt={`Property image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
